import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError, api, type Me, type RowQuery, type UserStatus } from "./api";

export const keys = {
  me: ["me"] as const,
  watches: ["watches"] as const,
  company: (siren: string) => ["company", siren] as const,
  summary: (siren: string) => ["summary", siren] as const,
  search: (q: string) => ["search", q] as const,
  categories: ["categories"] as const,
  imports: ["imports"] as const,
  importJob: (id: number) => ["imports", id] as const,
  exports: ["exports"] as const,
  registration: ["registration"] as const,
  recipients: ["recipients"] as const,
  alertLog: ["alert-log"] as const,
  admin: ["admin"] as const,
};

export function useMe() {
  return useQuery<Me | null>({
    queryKey: keys.me,
    queryFn: () =>
      api.me().catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }),
    staleTime: 5 * 60_000,
  });
}

export function useEventKinds() {
  return useQuery({ queryKey: ["event-kinds"], queryFn: api.eventKinds, staleTime: Infinity });
}

export function useSetAlertKinds() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.setAlertKinds,
    onSuccess: (me) => {
      client.setQueryData(keys.me, me);
      // "Alerts this month" only counts the kinds the user receives.
      client.invalidateQueries({ queryKey: keys.watches });
    },
  });
}

export function useRecipients() {
  return useQuery({ queryKey: keys.recipients, queryFn: api.recipients });
}

export function useAddRecipient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.addRecipient,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.recipients }),
  });
}

export function useRemoveRecipient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; email: string }) => api.removeRecipient(id),
    // Also on error: a 404 means the address already removed itself (link in a digest).
    onSettled: () => client.invalidateQueries({ queryKey: keys.recipients }),
  });
}

export function useResendRecipient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; email: string }) => api.resendRecipient(id),
    // A 409 or 404 means the list is stale (address confirmed or removed meanwhile).
    onError: () => client.invalidateQueries({ queryKey: keys.recipients }),
  });
}

export function useReactivateRecipient() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.reactivateRecipient,
    onSuccess: (list) => {
      client.setQueryData(keys.recipients, list);
      // The account e-mail may have been the suppressed one (me.alert_suppressed).
      client.invalidateQueries({ queryKey: keys.me });
    },
    // E.g. 404: an administrator lifted the block meanwhile.
    onError: () => {
      client.invalidateQueries({ queryKey: keys.recipients });
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useAlertLog(page: number, pageSize: number) {
  return useQuery({
    queryKey: [...keys.alertLog, page, pageSize],
    queryFn: () => api.alertLog(page, pageSize),
    placeholderData: keepPreviousData,
  });
}

export function useWatches() {
  return useQuery({ queryKey: keys.watches, queryFn: api.watches });
}

export function useCompany(siren: string) {
  return useQuery({
    queryKey: keys.company(siren),
    queryFn: () => api.company(siren),
    // The first sync runs in the background right after a company is added.
    refetchInterval: (query) =>
      query.state.data && !query.state.data.watch.last_checked_at ? 3000 : false,
  });
}

export function useCompanySummary(siren: string | null) {
  return useQuery({
    queryKey: keys.summary(siren ?? ""),
    queryFn: () => api.summary(siren!),
    enabled: siren !== null,
    staleTime: 10 * 60_000,
  });
}

export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useCompanySearch(query: string) {
  const q = useDebounced(query.trim());
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api.search(q),
    enabled: q.length >= 2,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useAddWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.createWatch,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.watches });
      client.invalidateQueries({ queryKey: keys.categories });
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useUpdateWatch(siren: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...change }: { id: number; label?: string; category_ids?: number[] }) =>
      api.updateWatch(id, change),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.watches });
      client.invalidateQueries({ queryKey: keys.categories });
      client.invalidateQueries({ queryKey: keys.company(siren) });
    },
  });
}

export function useBulkWatches() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.bulkWatches,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.watches });
      client.invalidateQueries({ queryKey: keys.categories });
      client.invalidateQueries({ queryKey: keys.me });
      client.removeQueries({ queryKey: ["company"] });
    },
  });
}

export function useCategories() {
  return useQuery({ queryKey: keys.categories, queryFn: api.categories });
}

function useCategoryMutation<V, R>(fn: (vars: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.categories });
      client.invalidateQueries({ queryKey: keys.watches });
      client.invalidateQueries({ queryKey: ["company"] });
    },
  });
}

export const useCreateCategory = () => useCategoryMutation(api.createCategory);
export const useRenameCategory = () =>
  useCategoryMutation(({ id, name }: { id: number; name: string }) => api.renameCategory(id, name));
export const useDeleteCategory = () => useCategoryMutation(api.deleteCategory);

export function useImports() {
  return useQuery({
    queryKey: keys.imports,
    queryFn: api.imports,
    refetchInterval: (query) => (query.state.data?.some((j) => j.status !== "done") ? 4000 : false),
  });
}

export function useImportJob(id: number | null) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: keys.importJob(id ?? 0),
    queryFn: () => api.importJob(id!),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.status === "done" ? false : 1500),
  });
  const done = query.data?.status === "done";
  useEffect(() => {
    if (!done) return;
    // Once, when the job finishes. `exact` keeps this job's own query out of it.
    client.invalidateQueries({ queryKey: keys.watches });
    client.invalidateQueries({ queryKey: keys.categories });
    client.invalidateQueries({ queryKey: keys.imports, exact: true });
    client.invalidateQueries({ queryKey: keys.me });
  }, [done, client]);
  return query;
}

export function useExports() {
  return useQuery({
    queryKey: keys.exports,
    queryFn: api.exports,
    refetchInterval: (query) =>
      query.state.data?.some((j) => j.status === "pending" || j.status === "running") ? 2000 : false,
  });
}

export function useStartExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.startExport,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.exports }),
  });
}

export function useRemoveWatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; siren: string }) => api.removeWatch(id),
    onSuccess: (_data, { siren }) => {
      client.invalidateQueries({ queryKey: keys.watches });
      client.invalidateQueries({ queryKey: keys.me });
      client.removeQueries({ queryKey: keys.company(siren) });
    },
  });
}

/** Signing in or out changes everything the cache knows. */
export function useSessionChange() {
  const client = useQueryClient();
  return (me: Me | null) => {
    client.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    client.setQueryData(keys.me, me);
  };
}

export function useRegistration() {
  return useQuery({ queryKey: keys.registration, queryFn: api.registration, staleTime: 60_000 });
}

// --- Admin -----------------------------------------------------------------------

export function useAdminStats() {
  return useQuery({ queryKey: [...keys.admin, "stats"], queryFn: api.admin.stats });
}

export function useAdminSettings() {
  return useQuery({ queryKey: [...keys.admin, "settings"], queryFn: api.admin.settings });
}

export function useAdminUsers(params: { q: string; status: UserStatus; page: number }) {
  return useQuery({
    queryKey: [...keys.admin, "users", params],
    queryFn: () => api.admin.users(params),
    placeholderData: keepPreviousData,
  });
}

export function useAdminTables() {
  return useQuery({ queryKey: [...keys.admin, "tables"], queryFn: api.admin.tables });
}

export function useAdminRows(table: string, query: RowQuery) {
  return useQuery({
    queryKey: [...keys.admin, "rows", table, query],
    queryFn: () => api.admin.rows(table, query),
    placeholderData: keepPreviousData,
  });
}

/** Any admin change can move counters anywhere in the admin area, and in the user's own views. */
export function useAdminMutation<V, R>(fn: (vars: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.admin });
      client.invalidateQueries({ queryKey: keys.registration });
      client.invalidateQueries({ queryKey: keys.me });
    },
  });
}
